import Encoding from "encoding-japanese";
import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import DataSvc from "../../../../services/dataSvc";
import { buildCsvFilename } from "../../../../utils/csvUtils";

import "./styles.scss";

interface ICheckOwnerListProps extends RouteComponentProps<any> {
  onLoading(loadingText: string | void): void;
  onLoaded(): void;
  commonErrorHandler: (err: string) => string | undefined;
}
const CheckOwnerList: React.FunctionComponent<ICheckOwnerListProps> = ({
  onLoading,
  onLoaded,
  commonErrorHandler,
}) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) => _t(`internaltoolsPage.checkOwnerList.${key}`),
    [_t]
  );
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isPerformingCheck, setIsPerformingCheck] = useState<boolean>(false);
  const [resultMessage, setResultMessage] = useState("");
  const [resultContent, setResultContent] = useState<string | undefined>(
    undefined
  );

  const fetchOwnerList = async () => {
    if (!selectedFile) {
      setErrorMessage(t("please_attach_file"));
      return;
    }

    setResultMessage("");
    setIsPerformingCheck(true);
    onLoading(t("do_not_close_window"));

    const formData = new FormData();
    formData.append("file", selectedFile);

    await DataSvc.checkOwnerList(formData)
      .then((data) => {
        const { csvString } = data;

        if (!csvString) {
          setErrorMessage(t("can_not_get_scores"));
          return;
        }
        setResultContent(csvString);

        setResultMessage(t("fetch_complete"));
      })
      .catch((err) => {
        const filteredError = commonErrorHandler(err) ?? "";
        setErrorMessage(filteredError ?? "");
      })
      .finally(() => {
        onLoaded();
        setIsPerformingCheck(false);
      });
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      if (isPerformingCheck) {
        event.returnValue = t("still_fetching");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPerformingCheck, t]);

  const onSelectedFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files: FileList | null = e.currentTarget.files;
    setSelectedFile(files !== null ? files[0] : null);
  };

  const downloadToCsv = () => {
    if (!resultContent) {
      return;
    }

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        contentDescription: "check_owner_list",
      },
      _t
    );

    const blob = new Blob([resultContent], { type: "text/csv" });
    const file = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", file);
    link.setAttribute("download", `${filename}.csv`);

    document.body.appendChild(link);
    link.click();
  };

  const downloadToCsvWindows = () => {
    if (!resultContent) {
      return;
    }

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        contentDescription: "check_owner_list",
      },
      _t
    );

    const unicodeArray = Encoding.stringToCode(resultContent);
    const sjisArray = Encoding.convert(unicodeArray, {
      to: "SJIS",
      from: "UNICODE",
      type: "array",
    });

    const blob = new Blob([new Uint8Array(sjisArray)], { type: "text/csv" });
    const file = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", file);
    link.setAttribute("download", `${filename}(日本語文字化け対応).csv`);

    document.body.appendChild(link);
    link.click();
  };

  return (
    <React.Fragment>
      <h1 className="main-title">{t("main_title")}</h1>
      <h2 className="sub-title">{t("sample")}</h2>
      <section className="card">
        <div className="sample-links">
          <a
            className="btn btn-border"
            href="/samples/owner_check_sample_upload.csv"
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            Download
          </a>
          <a
            className="btn btn-border"
            href="/samples/owner_check_sample_upload_windows.csv"
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            Download (Japanese character support)
          </a>
        </div>
      </section>
      <h2 className="sub-title">{t("input")}</h2>
      <section className="card check-owner-list-input">
        <p>{t("section_1_title_1")}</p>
        <input
          onChange={onSelectedFile}
          className="input"
          type="file"
          id="file"
          name="file"
          placeholder="CSV"
          accept=".csv"
        />
        <button className="btn btn-blue" type="button" onClick={fetchOwnerList}>
          {t("execute")}
        </button>
        {errorMessage && <h3 id="error">{errorMessage.toString()}</h3>}
      </section>
      <h2 className="sub-title">{t("result_message")}</h2>
      {resultMessage && (
        <section className="card result-section">
          <p>{resultMessage}</p>
          <div className="buttons">
            <button className="btn btn-border" onClick={downloadToCsv}>
              <i className="icons icon-saveload icon-saveload-blue"></i>
              Download
            </button>
            <button className="btn btn-border" onClick={downloadToCsvWindows}>
              <i className="icons icon-saveload icon-saveload-blue"></i>
              Download (Japanese character support)
            </button>
          </div>
        </section>
      )}
    </React.Fragment>
  );
};

export default withRouter(CheckOwnerList);
